// Tenant-agnostic entry for an AI approval link (/ai-approval/:id).
//
// MCP hands the AI a link without knowing the person's workspace address, so
// this forwards a signed-in person to their own workspace's review page, and
// parks the link for the sign-in flow to resume otherwise — the same pattern
// as /oauth/authorize and /activate.
import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { fetchSession } from '@/lib/session';
import { getCurrentTenant } from '@/lib/hrApi';
import { PENDING_AUTHORIZE_KEY, storedTenantSlug } from '../oauth/OAuthAuthorizePage';

export const AiApprovalEntryPage: React.FC = () => {
  const { id = '' } = useParams();
  const [state, setState] = useState<{ status: 'checking' | 'authed' | 'anon'; slug: string | null }>({
    status: 'checking', slug: null,
  });

  useEffect(() => {
    let active = true;
    (async () => {
      const user = await fetchSession();
      if (!active) return;
      let slug = storedTenantSlug();
      if (user && !slug) {
        try {
          slug = (await getCurrentTenant())?.slug || null;
        } catch {
          slug = null;
        }
      }
      if (!active) return;
      setState(user && slug ? { status: 'authed', slug } : { status: 'anon', slug: null });
    })();
    return () => { active = false; };
  }, []);

  if (state.status === 'checking') return null;
  if (state.status === 'authed' && state.slug) {
    sessionStorage.removeItem(PENDING_AUTHORIZE_KEY);
    return <Navigate to={`/t/${state.slug}/app/ai-approvals/${encodeURIComponent(id)}`} replace />;
  }
  try {
    sessionStorage.setItem(PENDING_AUTHORIZE_KEY, `/ai-approval/${encodeURIComponent(id)}`);
  } catch {
    // storage unavailable — the person can open the link again after signing in
  }
  return <Navigate to="/login" replace />;
};

export default AiApprovalEntryPage;
